/**
 * chronometry.js — GRANULLO_SILENT_CHRONOMETRY_V1
 *
 * Passive time-to-certainty instrumentation for the proof journey.
 * Invisible to the visitor. Priceless to the founder.
 *
 * Tracks:
 * - Step entry timestamps
 * - Dwell time per step
 * - Persona-specific timing
 * - Full journey completion time
 * - Friction spikes (dwell-time anomalies)
 * - Abandonment step
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Session-level chronometry state
let sessionData = null;

function initSession(persona) {
  sessionData = {
    persona,
    startedAt: Date.now(),
    steps: [],
    currentStep: null,
    currentStepEntry: null,
  };
}

function recordStepEntry(stepIndex, stepLabel) {
  if (!sessionData) return;

  // Close previous step if any
  if (sessionData.currentStep !== null && sessionData.currentStepEntry) {
    const dwell = Date.now() - sessionData.currentStepEntry;
    sessionData.steps.push({
      step: sessionData.currentStep,
      label: sessionData.currentStepLabel || '',
      dwell,
      enteredAt: sessionData.currentStepEntry,
    });

    // Fire live step event for real-time observability
    flushLiveStep({
      persona: sessionData.persona,
      prevStep: sessionData.currentStep,
      prevLabel: sessionData.currentStepLabel,
      dwell,
      nextStep: stepIndex,
      nextLabel: stepLabel,
      elapsed: Date.now() - sessionData.startedAt,
    });
  }

  sessionData.currentStep = stepIndex;
  sessionData.currentStepLabel = stepLabel;
  sessionData.currentStepEntry = Date.now();
}

function endSession(completed) {
  if (!sessionData) return null;

  // Close final step
  if (sessionData.currentStep !== null && sessionData.currentStepEntry) {
    const dwell = Date.now() - sessionData.currentStepEntry;
    sessionData.steps.push({
      step: sessionData.currentStep,
      label: sessionData.currentStepLabel || '',
      dwell,
      enteredAt: sessionData.currentStepEntry,
    });
  }

  const totalTime = Date.now() - sessionData.startedAt;
  const result = {
    persona: sessionData.persona,
    startedAt: new Date(sessionData.startedAt).toISOString(),
    totalTime,
    completed,
    stepsVisited: sessionData.steps.length,
    steps: sessionData.steps.map(s => ({
      step: s.step,
      label: s.label,
      dwell: s.dwell,
    })),
  };

  // Fire-and-forget telemetry
  flushChronometry(result);

  sessionData = null;
  return result;
}

async function flushChronometry(data) {
  try {
    await fetch(`${API_URL}/api/analytics/chronometry`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  } catch {
    // Silent — never block the user
  }
}

async function flushLiveStep(data) {
  try {
    await fetch(`${API_URL}/api/analytics/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        events: [{
          event: 'chronometry.live_step',
          data,
          timestamp: new Date().toISOString(),
        }],
      }),
    });
  } catch {
    // Silent
  }
}

function getSession() {
  return sessionData;
}

export const chronometry = {
  initSession,
  recordStepEntry,
  endSession,
  getSession,
};
